import { useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { ArrowClockwise, ArrowLeft, CheckCircle, Phone, QrCode } from '@phosphor-icons/react'
import { useAuth } from '@/auth/context'
import { cn } from '@/lib/cn'
import { Button } from '@/ui'
import { errorMessage } from '../api/core'
import {
  maskAccount,
  useApproveBankAccount,
  useBankAccounts,
  usePayout,
  usePayouts,
  type BankAccount,
  type PayoutRow,
} from '../api/payouts'
import { useUser } from '../api/reference'
import { ActionButton } from '../components/Can'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { QueryState } from '../components/Layout'
import { Money, UserName } from '../components/bits'
import {
  BoardColumns,
  BoardPage,
  BoardTable,
  FactRow,
  Facts,
  LinkButton,
  Note,
  PanelActions,
  Pill,
  SidePanel,
  Tag,
  TwoLine,
  type BoardColumn,
} from '../components/board'
import {
  boardDate,
  boardWhen,
  isFailed,
  openFailures,
  payoutPill,
  pickAccounts,
} from '../model/payoutBoard'
import { useUrlState } from '../lib/useUrlState'
import { VerifyDialog } from './FarmersPage'

const acc = (a: Pick<BankAccount, 'accountLast4' | 'ifsc'>) =>
  `${maskAccount(a.accountLast4).replace(' ', '')} · ${a.ifsc ?? '—'}`

interface HistoryRow {
  key: string
  at: string
  what: ReactNode
  who: ReactNode
}

/**
 * <farmer> · <roster no.> · bank account (alignment map V1-F4-B4c FixBank; F4-B4.c, F4.9-n): the
 * old and new account side by side, the OnGrid result, the name at the bank against the roster,
 * what happened to the account, lots waiting for it, and second approval. Only the last four,
 * IFSC and bank name are shown. `?payout=` is the failure that led here.
 */
export function FarmerBankPage() {
  const { id } = useParams()
  const [f] = useUrlState({ payout: '' })
  const { user } = useAuth()
  const farmer = useUser(id ?? null)
  const accounts = useBankAccounts(id)
  const failure = usePayout(f.payout || null)
  const payouts = usePayouts({ farmerId: id, page: 1, limit: 100 }, Boolean(id))
  const approve = useApproveBankAccount(id ?? '')
  const [verify, setVerify] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [nameMatches, setNameMatches] = useState<boolean | null>(null)

  const list = accounts.data ?? []
  const { old, fresh } = pickAccounts(list)
  const name = farmer.data?.name ?? 'Farmer'
  const code = farmer.data?.farmerCode
  const phone = farmer.data?.contactNo ?? null
  const p = failure.data ?? null
  const backTo = p ? `/c/payouts/failed?open=${p.id}` : `/c/farmers/${id}`
  const pending = fresh?.status === 'PENDING' ? fresh : null
  const approved = fresh && fresh.status === 'VERIFIED' && fresh.isPrimary ? fresh : null
  const startedByMe = Boolean(user && pending && pending.addedById === user.id)

  const approveBlocked = !pending
    ? 'No new account is waiting: verify its passbook details first.'
    : startedByMe
      ? 'You started this check: a second person must approve the account.'
      : !pending.nameAtBank
        ? 'OnGrid returned no account-holder name to compare.'
        : nameMatches !== true
          ? 'Compare the name at the bank with our roster and set “Name matches” first.'
          : null

  // What happened to his account, from the accounts themselves (calls and SMS are not kept here).
  const history: HistoryRow[] = []
  for (const a of list) {
    if (a.createdAt)
      history.push({
        key: `${a.id}-c`,
        at: a.createdAt,
        what: `OnGrid verification requested for a new account`,
        who: <UserName id={a.addedById} />,
      })
    if (a.verifiedAt)
      history.push({
        key: `${a.id}-v`,
        at: a.verifiedAt,
        what: `OnGrid verified ${maskAccount(a.accountLast4).replace(' ', '')}${a.nameAtBank ? ` · name at bank ${a.nameAtBank}` : ''}`,
        who: 'System',
      })
    if (a.approvedAt)
      history.push({
        key: `${a.id}-a`,
        at: a.approvedAt,
        what: `${maskAccount(a.accountLast4).replace(' ', '')} approved as his account · SMS to his phone that the account changed`,
        who: <UserName id={a.approvedById} />,
      })
    if (a.disabledAt)
      history.push({
        key: `${a.id}-d`,
        at: a.disabledAt,
        what: `${maskAccount(a.accountLast4).replace(' ', '')} switched off${a.disabledReason ? `: ${a.disabledReason}` : ''}`,
        who: 'System',
      })
  }
  if (p && isFailed(p.status) && (p.processedAt ?? p.requestedAt))
    history.push({
      key: `${p.id}-f`,
      at: (p.processedAt ?? p.requestedAt)!,
      what: `${p.unitCode} try ${p.attemptNo} ${p.status === 'REVERSED' ? 'returned' : 'failed'}: ${p.failureReason ?? 'no reason given'}`,
      who: 'System',
    })
  history.sort((a, b) => a.at.localeCompare(b.at))

  // Lots waiting for this account: open failures and payouts held for want of a verified account.
  const rows = payouts.data?.rows ?? []
  const waiting: Array<{ p: PayoutRow; failed: boolean }> = [
    ...openFailures(
      rows.filter((r) => isFailed(r.status)),
      null,
      rows,
    ).map((x) => ({ p: x.payout, failed: true })),
    ...rows
      .filter(
        (r) =>
          r.status === 'PENDING_APPROVAL' &&
          r.blockers.some((b) => b.code === 'NO_VERIFIED_ACCOUNT'),
      )
      .map((r) => ({ p: r, failed: false })),
  ]
  const lotColumns: BoardColumn<{ p: PayoutRow; failed: boolean }>[] = [
    {
      id: 'lot',
      header: 'Lots waiting for this account',
      cell: ({ p: r, failed }) => (
        <TwoLine top={r.unitCode}>
          <Tag tone={r.kind === 'TOP_UP' ? 'help' : 'plain'}>
            {r.kind === 'TOP_UP' ? 'Top-up' : 'Lot payment'}
          </Tag>
          {!failed && r.collectionDay && (
            <Note>
              Delivered {boardDate(r.collectionDay)} · his slip says payment waits for his bank
              details
            </Note>
          )}
        </TwoLine>
      ),
    },
    {
      id: 'amount',
      header: 'Amount',
      align: 'right',
      cell: ({ p: r }) => <Money paise={r.amountPaise} className="font-sans" />,
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ p: r, failed }) =>
        failed ? <Pill tone="bad">{payoutPill(r).text}</Pill> : <Pill tone="wait">Waiting</Pill>,
    },
  ]

  return (
    <BoardPage
      title={`${name}${code ? ` · ${code}` : ''} · bank account`}
      actions={
        <>
          {phone ? (
            <a
              href={`tel:${phone}`}
              className="inline-flex h-10 items-center gap-2 rounded-chip border-[1.5px] border-[#8a827a] bg-surface px-[14px] text-[15px] font-bold hover:bg-paper-tint"
            >
              <Phone aria-hidden="true" /> Call farmer
            </a>
          ) : (
            <Button
              variant="secondary"
              leadingIcon={<Phone />}
              disabled
              title="No phone number on his roster entry"
            >
              Call farmer
            </Button>
          )}
          <LinkButton to={backTo} icon={<ArrowLeft aria-hidden="true" />}>
            {p ? 'Back to the failure' : 'Back to the farmer'}
          </LinkButton>
        </>
      }
    >
      <QueryState query={accounts} label="his bank accounts">
        <BoardColumns
          main={
            <>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <AccountCard
                  heading="Old account"
                  account={old}
                  tone={
                    old?.status === 'FAILED' ? 'bad' : old?.status === 'VERIFIED' ? 'ok' : 'idle'
                  }
                  pill={
                    old
                      ? old.status === 'FAILED'
                        ? 'Failed'
                        : old.status === 'DISABLED'
                          ? 'Switched off'
                          : old.status === 'VERIFIED'
                            ? 'In use'
                            : 'Waiting'
                      : '—'
                  }
                  sub={
                    old
                      ? [
                          old.nameAtBank,
                          old.status === 'FAILED' && p?.failureReason ? p.failureReason : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')
                      : null
                  }
                  note={
                    !old
                      ? 'No account on record yet.'
                      : old.status === 'FAILED'
                        ? 'Automatic payments stopped. Switched off when the new one is approved.'
                        : old.status === 'DISABLED'
                          ? `Switched off ${boardWhen(old.disabledAt)}${old.disabledReason ? `: ${old.disabledReason}` : ''}.`
                          : 'In use for his payments until a new account is approved.'
                  }
                />
                <AccountCard
                  heading="New account"
                  account={fresh}
                  tone={fresh?.nameAtBank ? 'ok' : 'wait'}
                  pill={
                    !fresh
                      ? '—'
                      : approved
                        ? 'Approved · in use'
                        : fresh.nameAtBank
                          ? 'OnGrid verified'
                          : 'Verification incomplete'
                  }
                  sub={
                    fresh
                      ? fresh.verifiedAt
                        ? boardWhen(fresh.verifiedAt)
                        : `started ${boardWhen(fresh.createdAt)}`
                      : null
                  }
                  note={
                    !fresh
                      ? 'No new account yet. Enter the passbook account number and IFSC for OnGrid verification.'
                      : 'Verified through OnGrid. The full number is encrypted server-side; screens show only the last four digits.'
                  }
                  empty={!fresh}
                />
              </div>
              <section className="flex flex-col gap-3 rounded-card border-[1.5px] border-rule bg-surface p-4">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <FactRow label="On our roster">{name}</FactRow>
                  <FactRow label="Name at bank">
                    <b>{fresh?.nameAtBank ?? '—'}</b>
                  </FactRow>
                </div>
                <div className="flex flex-wrap items-center gap-2.5">
                  <b className="text-[15px]">Name matches</b>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={nameMatches === true}
                    aria-label="Name matches"
                    disabled={!pending?.nameAtBank}
                    onClick={() => setNameMatches(nameMatches !== true)}
                    className={cn(
                      'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-40',
                      nameMatches === true ? 'bg-ok' : 'bg-[#8a827a]',
                    )}
                  >
                    <span
                      className={cn(
                        'absolute h-5 w-5 rounded-full bg-white transition-transform',
                        nameMatches === true ? 'translate-x-[22px]' : 'translate-x-0.5',
                      )}
                    />
                  </button>
                  <span className="text-[14px]">
                    {approved
                      ? 'Yes · approved'
                      : nameMatches === true
                        ? 'Yes'
                        : nameMatches === false
                          ? 'No'
                          : '—'}
                  </span>
                  <span className="flex-1" />
                  <Note>
                    Set by the person who approves, after comparing the two names. It is not stored.
                  </Note>
                </div>
              </section>
              <BoardTable<HistoryRow>
                caption="What happened to his account"
                columns={[
                  {
                    id: 'when',
                    header: 'When',
                    cell: (r) => <span className="whitespace-nowrap">{boardWhen(r.at)}</span>,
                  },
                  { id: 'what', header: 'What', cell: (r) => r.what },
                  { id: 'who', header: 'Who', cell: (r) => r.who, hideBelow: 'sm' },
                ]}
                rows={history}
                getRowId={(r) => r.key}
                empty="Nothing yet."
              />
              <BoardTable
                caption="Lots waiting for this account"
                columns={lotColumns}
                rows={waiting}
                getRowId={(r) => r.p.id}
                loading={payouts.isLoading}
                error={payouts.isError ? errorMessage(payouts.error) : undefined}
                empty="No lot is waiting for his account."
              />
            </>
          }
          side={
            <SidePanel label="Second approval" title="Second approval">
              {approved ? (
                <Pill tone="ok">Approved</Pill>
              ) : pending ? (
                <Pill tone="wait">Waiting for a second person</Pill>
              ) : (
                <Pill tone="idle">No check waiting</Pill>
              )}
              <Facts>
                <FactRow label="Started by">
                  {fresh ? (
                    <>
                      <UserName id={fresh.addedById} /> · {boardWhen(fresh.createdAt)}
                    </>
                  ) : (
                    '—'
                  )}
                </FactRow>
                <FactRow label="Name matches">
                  {approved
                    ? 'Yes'
                    : nameMatches === true
                      ? 'Yes · you'
                      : nameMatches === false
                        ? 'No'
                        : '—'}
                </FactRow>
                <FactRow label="Approved by">
                  {approved ? <UserName id={approved.approvedById} /> : '—'}
                </FactRow>
              </Facts>
              {user && pending && (
                <Pill tone={startedByMe ? 'bad' : 'ok'}>
                  <UserName id={user.id} /> (you)
                </Pill>
              )}
              <Note>
                {startedByMe
                  ? 'You started this check, so a second person must approve it.'
                  : 'You did not start this check, so you can approve it.'}{' '}
                One person alone cannot change a farmer’s account; every change sends an SMS to his
                own phone.
              </Note>
              <Note>
                Name does not match: do not approve. Confirm the passbook details with the farmer
                and run OnGrid verification again.
              </Note>
              <div className="flex-1" />
              <PanelActions>
                <ActionButton
                  perm="bank-accounts:verify"
                  variant="secondary"
                  leadingIcon={<QrCode />}
                  onClick={() => setVerify(true)}
                >
                  Verify account
                </ActionButton>
                <ActionButton
                  perm="bank-accounts:approve"
                  leadingIcon={<CheckCircle />}
                  blockedReason={approveBlocked}
                  onClick={() => setConfirming(true)}
                >
                  Approve new account
                </ActionButton>
                {approveBlocked && pending && <Note>{approveBlocked}</Note>}
                {p && approved && isFailed(p.status) ? (
                  <LinkButton
                    to={`/c/payouts/${p.id}/pay-again`}
                    icon={<ArrowClockwise aria-hidden="true" />}
                  >
                    Pay again
                  </LinkButton>
                ) : (
                  <Button
                    variant="secondary"
                    leadingIcon={<ArrowClockwise />}
                    disabled
                    title={
                      p
                        ? 'Open after the new account is approved'
                        : 'Open this screen from a failed payment'
                    }
                  >
                    Pay again · after approval
                  </Button>
                )}
              </PanelActions>
            </SidePanel>
          }
        />
      </QueryState>
      {id && <VerifyDialog open={verify} onClose={() => setVerify(false)} farmerId={id} />}
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Approve the new account"
        description="It becomes his account for payments; the old one is switched off and he gets an SMS that his account changed."
        confirmLabel="Approve new account"
        loading={approve.isPending}
        onConfirm={() =>
          pending ? approve.mutateAsync(pending.id).then(() => setConfirming(false)) : undefined
        }
      >
        {pending && (
          <span className="text-[14px]">
            {acc(pending)} · name at bank <b>{pending.nameAtBank ?? '—'}</b> · roster {name}
          </span>
        )}
      </ConfirmDialog>
    </BoardPage>
  )
}

function AccountCard({
  heading,
  account,
  tone,
  pill,
  sub,
  note,
  empty,
}: {
  heading: string
  account: BankAccount | null
  tone: 'ok' | 'bad' | 'wait' | 'idle'
  pill: string
  sub: string | null
  note: string
  empty?: boolean
}) {
  return (
    <section
      className={cn(
        'flex flex-col gap-2 rounded-card bg-surface p-4',
        empty
          ? 'border-[1.5px] border-dashed border-[#8a827a]'
          : tone === 'bad'
            ? 'border-2 border-danger'
            : tone === 'ok'
              ? 'border-2 border-ok'
              : 'border-[1.5px] border-rule',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <b className="text-[16px]">{heading}</b>
        {account && <Pill tone={tone}>{pill}</Pill>}
      </div>
      {account && (
        <b className="text-[17px]">
          {acc(account)}
          {sub && <span className="block text-[14px] font-medium">{sub}</span>}
        </b>
      )}
      <Note>{note}</Note>
    </section>
  )
}
